import React, { useState, useEffect, useMemo } from "react";
import {
  Layout,
  Card,
  Divider,
  Typography,
  Button,
  Space,
  Row,
  Col,
  message,
  Modal,
  Spin,
} from "antd";
import { CreditCardOutlined, LockOutlined } from "@ant-design/icons";
import paymentService from "../../services/payment";
import DefaultLayout from "../../components/layout/DefaultLayout";

const { Content } = Layout;
const { Text, Title } = Typography;

// 🔹 Shipping calculation function
const calculateShipping = (weight, miles) => {
  const w = parseFloat(weight) || 0;
  let basePrice = 0;

  // Weight-based pricing
  if (w <= 40) basePrice = 30;
  else if (w <= 50) basePrice = 35;
  else if (w <= 60) basePrice = 40;
  else if (w <= 70) basePrice = 45;
  else if (w <= 80) basePrice = 50;
  else if (w <= 90) basePrice = 55;
  else if (w <= 100) basePrice = 60;
  else if (w <= 110) basePrice = 65;
  else if (w <= 120) basePrice = 70;
  else if (w <= 130) basePrice = 75;
  else if (w <= 140) basePrice = 80;
  else if (w <= 150) basePrice = 85;
  else basePrice = w * 0.55;

  // Miles-based pricing
  const milesNumber = Math.floor(parseFloat(miles) || 0);
  let milesExtra = 0;
  if (milesNumber > 10) milesExtra = (milesNumber - 10) * 1.5;

  return basePrice + milesExtra;
};

const Checkout = () => {
  const [loading, setLoading] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [squareCard, setSquareCard] = useState(null);

  // 🔹 Load cart from localStorage
  useEffect(() => {
    const storedCart = localStorage.getItem("cartItems");
    if (storedCart) setCartItems(JSON.parse(storedCart));
  }, []);

  // 🔹 Save/clear cart in localStorage
  useEffect(() => {
    if (cartItems.length > 0) localStorage.setItem("cartItems", JSON.stringify(cartItems));
    else localStorage.removeItem("cartItems");
  }, [cartItems]);

  // 🔹 Calculate totals per customer & grand total
  const totals = useMemo(() => {
    let grandTotal = 0;
    const customerTotals = cartItems.map((customer) => {
      // Total weight
      const totalWeight = customer.boxes.reduce((sum, box) => sum + parseFloat(box.weight || 0), 0);

      // Rounded miles
      let milesValue = parseFloat(customer.miles) || 0;
      milesValue = milesValue % 1 < 0.5 ? Math.floor(milesValue) : Math.ceil(milesValue);

      // Subtotal for customer
      let subtotal = calculateShipping(totalWeight, milesValue);

      // Add 5% surcharge
      subtotal *= 1.05;

      grandTotal += subtotal;
      return { ...customer, totalWeight, milesValue, subtotal };
    });

    return { customerTotals, grandTotal };
  }, [cartItems]);

  // 🔹 Load Square SDK
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://web.squarecdn.com/v1/square.js";
    script.async = true;
    script.onload = () => initSquare();
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  const initSquare = async () => {
    if (!window.Square) return;
    try {
      const payments = window.Square.payments(
        "sq0idp-3P3Ba2sTasNm1PDyJGxs7g",
        "75YEZ3EX1V5VJ"
      );
      const card = await payments.card();
      await card.attach("#card-container");
      setSquareCard(card);
    } catch (err) {
      console.error("Square init error:", err);
    }
  };

  // 🔹 Payment handler
  const handlePayment = async (amount, customer = null) => {
    if (!squareCard) {
      message.error("Payment form not ready.");
      return;
    }

    setLoading(true);

    try {
      const result = await squareCard.tokenize();
      if (result.status === "OK") {
        const payload = {
          token: result.token,
          amount,
          customer,
          cart: customer ? [customer] : cartItems,
        };

        const data = await paymentService.postPayment(payload);

        if (customer) {
          setCartItems((prev) =>
            prev.filter((c) => c.customerName !== customer.customerName)
          );

          Modal.success({
            title: "Thank you!",
            content:
              data.message || `Payment successful for ${customer.customerName}.`,
          });
        } else {
          setCartItems([]);

          Modal.success({
            title: "Thank you!",
            content: data.message || "Payment successful for all customers.",
          });
        }
      } else {
        message.error("Payment failed.");
      }
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message || "Payment error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f4dba7e6" }}>
      {/* <Banner /> */}
      <DefaultLayout>

      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <Spin size="large" tip="Processing Payment..." />
        </div>
      )}

      <Content className="checkout-content" style={{ padding: "32px 64px", maxWidth: "100%", margin: "0 auto" }}>
        <Title level={3} style={{ marginBottom: 24 }}>
          Checkout
        </Title>

        {cartItems.length === 0 ? (
          <Text type="secondary">Your cart is empty.</Text>
        ) : (
            <Row gutter={[16, 16]} align="start" wrap>
            {/* Left Column → Individual Customers */}
            <Col xs={24} lg={12}>
              <Space direction="vertical" style={{ width: "100%" }} size="large">
                {totals.customerTotals.map((customer, cIndex) => (
                  <Card
                    key={cIndex}
                    title={`Customer: ${customer.customerName}`}
                    bordered
                    style={{ borderRadius: 12, boxShadow: "0 2px 6px rgba(0,0,0,0.05)", padding: '2px' }}
                  >
                    {/* <p>📱 WhatsApp: {customer.whatsapp}</p>
                    <p>🏠 Address: {customer.address}</p> */}
                    {/* <Divider /> */}

                    {customer.boxes.map((box, bIndex) => (
                      <Row key={bIndex} justify="space-between" style={{ marginTop: '-4px' }}>
                        <Col>
                          Box {bIndex + 1} → {box.length} × {box.width} × {box.height} • wt {box.weight} lbs
                        </Col>
                        {/* <Col>
                          <Text>${calculateShipping(box.weight, customer.miles).toFixed(2)}</Text>
                        </Col> */}
                      </Row>
                    ))}

                    <Divider />
                    <Row justify="space-between">
                      <Col><Text strong>Total Weight:</Text></Col>
                      <Col><Text>{customer.totalWeight} lbs</Text></Col>
                    </Row>
                    <Row justify="space-between">
                      <Col><Text strong>Distance:</Text></Col>
                      <Col><Text>{customer.milesValue} mi</Text></Col>
                    </Row>
                    <Row justify="space-between" style={{ marginTop: 8 }}>
                      <Col><Text strong>Subtotal:</Text></Col>
                      <Col><Text strong>${customer.subtotal.toFixed(2)}</Text></Col>
                    </Row>

                    {/* <Button
                      type="primary"
                      block
                      style={{ marginTop: 12 }}
                      disabled={loading}
                      onClick={() => handlePayment(customer.subtotal, customer)}
                    >
                      Pay for {customer.customerName}
                    </Button> */}
                  </Card>
                ))}
              </Space>
            </Col>

            {/* Right Column → Pay All */}
            <Col xs={24} lg={12}>
              <Card
                bordered
                style={{
                  borderRadius: 12,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                  position: "sticky",
                  top: 24,
                }}
              >
                <Title level={4} style={{ marginBottom: 16 }}>
                  <CreditCardOutlined /> Payment
                </Title>

                <div id="card-container" style={{ marginBottom: 16 }}></div>

                <Divider />
                <Row justify="space-between" style={{ marginBottom: 16 }}>
                  <Col><Title level={5}>Grand Total:</Title></Col>
                  <Col><Title level={5}>${totals.grandTotal.toFixed(2)}</Title></Col>
                </Row>

                <Button
                  type="primary"
                  size="large"
                  block
                  disabled={loading}
                  onClick={() => handlePayment(totals.grandTotal, null)}
                >
                  Pay All
                </Button>

                <div style={{ marginTop: 16, textAlign: "center" }}>
                  <Row gutter={16} justify="center" style={{ marginBottom: 8 }}>
                    <Col><img src="https://img.icons8.com/color/48/visa.png" alt="Visa" /></Col>
                    <Col><img src="https://img.icons8.com/color/48/mastercard-logo.png" alt="MasterCard" /></Col>
                    <Col><img src="https://img.icons8.com/color/48/amex.png" alt="AmEx" /></Col>
                    <Col><img src="https://img.icons8.com/color/48/discover.png" alt="Discover" /></Col>
                  </Row>
                  <Text type="secondary"><LockOutlined /> Your payment is secure and encrypted</Text>
                </div>
              </Card>
            </Col>
          </Row>
        )}
      </Content>
      {/* <AppFooter /> */}
      </DefaultLayout>
    </Layout>
  );
};

export default Checkout;
